import React from 'react'

import { ScrollView } from 'react-native'

import { useTheme } from '~/hooks'
import { type ScopedProps, useOptionMenuContext } from '../../option-menu'

import { Group } from '../group'

import { isItemElement } from '../../utils/is-item-element'

import { guid } from '~/utils/guid'

type ListProps = {
  children: React.ReactNode
  testID?: string
}

const SCOPE_ERROR_MESSAGE =
  'Only Fragment, OptionMenu.Item and Group components are allowed as children of OptionMenu.List. Please remove or replace any other elements.'

export function List(props: ListProps) {
  const { __scopeOptionMenu, children, testID } =
    props as ScopedProps<ListProps>

  const theme = useTheme()

  const { type } = useOptionMenuContext('OptionMenuList', __scopeOptionMenu)

  let childItems: React.ReactNode[] = []

  React.Children.forEach(children, child => {
    if (!React.isValidElement(child)) return

    if (child.type === React.Fragment) {
      const fragmentChildren = child.props.children as React.ReactNode
      childItems = [...childItems, fragmentChildren]

      return
    }

    if (isItemElement(child) || child.type === Group) {
      childItems.push(child)
    } else {
      throw new Error(SCOPE_ERROR_MESSAGE)
    }
  })

  childItems = childItems.map(child => {
    if (isItemElement(child)) {
      return React.cloneElement(child, {
        key: guid(),
        type,
      })
    }

    return child
  })

  return (
    <ScrollView
      contentContainerStyle={{
        paddingVertical: theme.spacingInlineOnepulse,
      }}
      bounces={false}
      testID={testID}
    >
      {childItems}
    </ScrollView>
  )
}
